import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  ClockIcon, 
  PlayIcon, 
  StopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

export default function AutomatedScanningConfig({ domain, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [availableFrequencies, setAvailableFrequencies] = useState([]);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [planInfo, setPlanInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAutomatedScanStatus();
    fetchPlanInfo();
    
    // Set up an interval to refresh scan status every 5 seconds
    // This ensures the last scan date and next scan time are updated on the page quickly
    const refreshInterval = setInterval(() => {
      fetchAutomatedScanStatus();
    }, 5000);
    
    // Clean up the interval when the component unmounts
    return () => clearInterval(refreshInterval);
  }, [domain.id]);

  const fetchAutomatedScanStatus = async () => {
    try {
      // Fetch with cache busting to ensure fresh data
      const response = await api.getAutomatedScanStatus(domain.id); // Use cache-busting method
      
      // Store previous values for comparison
      const prevLastAutomatedScan = scanStatus?.last_automated_scan;
      const prevNextScheduledScan = scanStatus?.next_scheduled_scan;
      
      // Update local state
      setScanStatus(response.data);
      setSelectedFrequency(response.data.scan_frequency_minutes.toString());
      setAvailableFrequencies(response.data.available_frequencies);
      setError('');
      
      // Check if automated scan timestamp has changed
      const automatedScanCompleted = 
        prevLastAutomatedScan && 
        response.data.last_automated_scan && 
        new Date(prevLastAutomatedScan).getTime() !== new Date(response.data.last_automated_scan).getTime();
      
      // Check if next scheduled scan has changed
      const scheduleChanged = 
        prevNextScheduledScan && 
        response.data.next_scheduled_scan && 
        new Date(prevNextScheduledScan).getTime() !== new Date(response.data.next_scheduled_scan).getTime();
      
      // If automated scan completed or schedule changed, refresh parent component
      if ((automatedScanCompleted || scheduleChanged) && onUpdate && typeof onUpdate === 'function') {
        console.log('Automated scan completed or schedule changed, refreshing domain data');
        onUpdate();
      }
      
      // Only force refresh if we haven't already triggered one above
      // and we have a last_automated_scan value that's recent (within last 5 minutes)
      if (!automatedScanCompleted && !scheduleChanged && 
          response.data.last_automated_scan && onUpdate && typeof onUpdate === 'function') {
        const lastScanTime = new Date(response.data.last_automated_scan).getTime();
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        
        if (lastScanTime > fiveMinutesAgo) {
          console.log('Recent automated scan detected, forcing domain data refresh');
          onUpdate();
        }
      }
    } catch (err) {
      setError('Failed to load automated scanning status');
      console.error('Error fetching scan status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanInfo = async () => {
    try {
      const response = await api.get('/automated-scanning/plan-info');
      setPlanInfo(response.data);
    } catch (err) {
      console.error('Error fetching plan info:', err);
    }
  };

  const handleEnableAutomatedScanning = async () => {
    if (!selectedFrequency) {
      setError('Please select a scan frequency');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/automated-scanning/enable', {
        domain_id: domain.id,
        frequency_minutes: parseInt(selectedFrequency)
      });

      setSuccess(response.data.message);
      await fetchAutomatedScanStatus();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enable automated scanning');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableAutomatedScanning = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/automated-scanning/disable/${domain.id}`);
      setSuccess(response.data.message);
      await fetchAutomatedScanStatus();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable automated scanning');
    } finally {
      setSaving(false);
    }
  };

  const handleTestScan = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/automated-scanning/test/${domain.id}`);
      setSuccess(`${response.data.message} - Check the Scans page to see the results!`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger test scan');
    } finally {
      setSaving(false);
    }
  };

  const formatFrequencyLabel = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  const formatNextScan = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    // Parse the UTC date string from the backend
    const date = new Date(dateString);
    const now = new Date();
    
    // Add a small buffer (10 seconds) to account for processing delays
    const bufferMs = 10 * 1000;
    const diffMs = date - now + bufferMs;
    
    if (diffMs < 0) {
      // Check if it's only slightly overdue (within 2 minutes) - could be due to scheduler delay
      if (Math.abs(diffMs) < 2 * 60 * 1000) {
        return 'Due now';
      }
      return 'Overdue';
    }
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `In ${diffHours}h ${diffMinutes}m`;
    } else {
      return `In ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  // Show upgrade message if automated scanning is not available
  if (planInfo && !planInfo.can_use_automated_scanning) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start">
          <InformationCircleIcon className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
              🚀 Automated Scanning - Available on Pro Plans
            </h4>
            <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
              Set up automatic typosquatting scans that run in the background. Get notified instantly when new threats are detected - no manual checking required!
            </p>
            <button
              onClick={() => window.location.href = '/plans'}
              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              Upgrade to Pro →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
      {/* Status Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ClockIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Automated Scanning
          </span>
          {scanStatus?.automated_scanning_enabled ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
              <CheckCircleIcon className="h-3 w-3 mr-1" />
              Enabled
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              Disabled
            </span>
          )}
        </div>
      </div>

      {/* Configuration */}
      {scanStatus && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-3">
          {scanStatus.automated_scanning_enabled ? (
            <>
              {/* Current Settings */}
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Frequency:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {formatFrequencyLabel(scanStatus.scan_frequency_minutes)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Next Scan:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {formatNextScan(scanStatus.next_scheduled_scan)}
                  </span>
                </div>
              </div>

              {/* Failure Warning */}
              {scanStatus.automated_scan_failures > 0 && (
                <div className="flex items-center space-x-2 text-sm text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span>
                    {scanStatus.automated_scan_failures} recent failure{scanStatus.automated_scan_failures > 1 ? 's' : ''}
                    {scanStatus.automated_scan_failures >= 3 && ' - Automated scanning may be disabled'}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDisableAutomatedScanning}
                  disabled={saving}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <StopIcon className="h-4 w-4 mr-1.5" />
                  {saving ? 'Disabling...' : 'Disable'}
                </button>

                {/* Test Scan Button */}
                <button
                  onClick={handleTestScan}
                  disabled={saving}
                  className="inline-flex items-center px-3 py-1.5 border border-indigo-300 dark:border-indigo-600 text-sm font-medium rounded-md text-indigo-700 dark:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Test Now
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Frequency Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Scan Frequency
                </label>
                <select
                  value={selectedFrequency}
                  onChange={(e) => setSelectedFrequency(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Select frequency...</option>
                  {availableFrequencies.map((freq) => (
                    <option key={freq.minutes} value={freq.minutes}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleEnableAutomatedScanning}
                disabled={saving || !selectedFrequency}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <PlayIcon className="h-4 w-4 mr-1.5" />
                {saving ? 'Enabling...' : 'Enable Automated Scanning'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}

      {success && (
        <div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2">
          {success}
        </div>
      )}
    </div>
  );
}
